/**
 * Author: Prajna
 * Date: 09/27/2024
 * Redesigned: 08/24/2026 — single-user-login-per-browser-profile
 *
 * Description:
 * AuthService is responsible for managing user authentication state. It uses a
 * BehaviorSubject to store and emit the login status across the application.
 *
 * The server's session (an HttpOnly cookie, validated against dbo.tblUserSession
 * on every request — see AccountController's session/status|logout endpoints and
 * SessionActivityMiddleware) is the actual source of truth; it can never be read
 * or written from here. What lives in localStorage below is a non-authoritative
 * display cache only — one writer (this service, `updateUserDetails`/`logout`),
 * always fully overwritten rather than merged, and always reconciled against the
 * server via `refresh()`. That single-writer/always-reconciled design is what
 * removes the "second login overwrites the first tab's state" bug at its root,
 * rather than patching symptoms of it.
 *
 * Key Responsibilities:
 * - Set login status when user logs in or logs out.
 * - Provide login status to components via an observable.
 * - Reconcile that status against the server (`refresh()`), never trusting
 *   localStorage as proof of being logged in.
 * - Keep every open tab of the same browser profile in sync via BroadcastChannel,
 *   so a login/logout in one tab is reflected in all others immediately.
 */
import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { RefineryPresenceService } from './services/refinery-presence.service';
import { UserService } from './services/user.service';
export interface UserDetails {
  firstName: string;
  lastName: string;
  userName: string;
  userType: number;
  id: string;
  userId: number;
  emailAddress: string;
}

type AuthBroadcastMessage = { type: 'login' | 'logout' };

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private loginStatusSubject = new BehaviorSubject<boolean>(this.checkLocalStorage()); // Optimistic initial value, corrected by refresh()
  public loginStatus$ = this.loginStatusSubject.asObservable(); // Expose as observable

    // Add BehaviorSubject for user details
  private userDetailsSubject = new BehaviorSubject<UserDetails | null>(this.loadUserDetails());
  public userDetails$ = this.userDetailsSubject.asObservable();

  // Fires only when a login is detected as having happened in a DIFFERENT tab of
  // this browser profile (via BroadcastChannel) - never for this tab's own login.
  // LoginComponent uses this to redirect away from an already-rendered login form
  // the instant another tab finishes logging in, covering the race where two tabs
  // both loaded the form before either one submitted. loginStatus$ can't be used
  // for this: it also fires on this tab's own local login, before this tab has
  // finished resolving which organization to act under.
  public remoteLogin$ = new Subject<void>();

  // BroadcastChannel is unavailable in a couple of edge contexts (very old
  // browsers, some sandboxed embeds) — degrade to single-tab-only sync rather
  // than throwing on construction.
  private broadcastChannel: BroadcastChannel | null = null;

  // True only once the current logged-in status has been confirmed by the server
  // (a refresh() that found a live session, or this tab's own successful login).
  // The initial value above comes from localStorage and is only a guess, so it
  // does NOT count. AuthGuard uses this to check the server once per page load
  // rather than on every navigation.
  private serverConfirmed = false;

  // Shares one in-flight session/status request among concurrent callers (the
  // bootstrap refresh in AppComponent and the first route guard both fire on page
  // load) so they cost one round trip, not two.
  private refreshInFlight: Promise<boolean> | null = null;

  constructor(private refineryPresence: RefineryPresenceService, private userService: UserService) {
    try {
      this.broadcastChannel = new BroadcastChannel('dtr-auth');
      this.broadcastChannel.onmessage = (event: MessageEvent<AuthBroadcastMessage>) => {
        this.applyRemoteStatus(event.data?.type === 'login');
      };
    } catch {
      this.broadcastChannel = null;
    }
  }

  // Check if user data exists in localStorage
  private checkLocalStorage(): boolean {
    return !!localStorage.getItem('userData'); // Returns true if userData exists
  }

  // Asks the server, authoritatively, whether this browser profile currently has
  // a live session, and reconciles local state (and every other open tab, via
  // updateUserDetails/logout below) to match. Called at app bootstrap and after
  // any 401, so a stale/tampered/expired local record can never keep a dead
  // session looking alive, or vice versa.
  refresh(): Promise<boolean> {
    if (!this.refreshInFlight) {
      this.refreshInFlight = this.refreshFromServer().finally(() => this.refreshInFlight = null);
    }
    return this.refreshInFlight;
  }

  // Resolves true only for a server-confirmed live session. Checks the server only
  // when the current status hasn't already been confirmed (see serverConfirmed).
  async ensureAuthenticated(): Promise<boolean> {
    if (this.serverConfirmed && this.getLoginStatus()) {
      return true;
    }
    return this.refresh();
  }

  // Pushes the server session's expiry forward while the user is active (driven by
  // SessionTimeoutService). If the server says the session is already gone (revoked
  // or expired), take the same logged-out path as refresh(). An unreachable server
  // changes nothing.
  async keepAlive(): Promise<void> {
    const status = await this.userService.keepAliveSession();
    if (status && !status.isAuthenticated) {
      this.clearLocalState();
      this.applyRemoteStatus(false);
    }
  }

  private async refreshFromServer(): Promise<boolean> {
    const status = await this.userService.getSessionStatus();
    if (status.isAuthenticated) {
      this.updateUserDetails({
        firstName: this.userDetailsSubject.value?.firstName ?? '',
        lastName: this.userDetailsSubject.value?.lastName ?? '',
        userName: status.userName ?? '',
        userType: this.userDetailsSubject.value?.userType ?? 0,
        id: String(status.userId ?? ''),
        userId: status.userId ?? 0,
        emailAddress: this.userDetailsSubject.value?.emailAddress ?? ''
      });
      this.setLoginStatus(true);
    } else {
      this.clearLocalState();
      this.applyRemoteStatus(false);
    }
    return status.isAuthenticated;
  }

  // Set login status. This is the single choke point both login (called from
  // LoginComponent) and logout (below) go through, so it's also the single place
  // that broadcasts the transition to every other open tab of this profile.
  setLoginStatus(status: boolean) {
    // Every caller passing true has just had the server confirm the session
    // (refresh() above, or LoginComponent after a successful login).
    this.serverConfirmed = status;
    this.loginStatusSubject.next(status);
    this.broadcastChannel?.postMessage({ type: status ? 'login' : 'logout' } as AuthBroadcastMessage);
  }

  // Applies a status change that originated elsewhere (another tab's broadcast,
  // or refresh() finding the server session already gone) without re-broadcasting
  // it — broadcasting here would ping-pong the message back and forth forever.
  private applyRemoteStatus(status: boolean) {
    if (this.loginStatusSubject.value === status) {
      return;
    }
    if (!status) {
      this.clearLocalState();
    }
    // A login broadcast from another tab hasn't been confirmed by this tab
    // (AuthGuard will check it on the next navigation); a logout always clears it.
    this.serverConfirmed = false;
    this.loginStatusSubject.next(status);
    if (status) {
      this.remoteLogin$.next();
    }
  }

  // Load user details from localStorage for initial BehaviorSubject value
  private loadUserDetails(): UserDetails | null {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null;
  }
  // Get login status
  getLoginStatus() {
    return this.loginStatusSubject.getValue();
  }
  // Update user details in both BehaviorSubject and localStorage
  updateUserDetails(user: UserDetails) {
    localStorage.setItem('userData', JSON.stringify(user));
    this.userDetailsSubject.next(user);
  }

  // Get user details
  getUserDetails(): { firstName: string, lastName: string, userName: string, userType: number, id: string ,userId:number,emailAddress:string} | null {
    const userData = localStorage.getItem('userData');
    return userData ? JSON.parse(userData) : null; // Parse the user details from localStorage
  }

  // Get user ID
  getUserId(): string | null {
    const userDetails = this.getUserDetails();
    return userDetails ? userDetails.id : null; // Return the user ID if available
  }

  private clearLocalState() {
    localStorage.removeItem('userData');
    localStorage.removeItem('userOrgData');
    localStorage.removeItem('userOrgs');
    localStorage.removeItem('selectedOrganizationId');
    localStorage.removeItem('selectedOrganizationName');
    this.userDetailsSubject.next(null);
  }

  // Logout user: revoke the session server-side first (so it's actually dead, not
  // just locally forgotten), then clear local state and notify every other tab.
  async logout() {
    // Release any refinery presence BEFORE clearing user data below — the
    // release request needs the still-valid userId to match what's stored
    // in the presence record, and the heartbeat timer would otherwise keep
    // running (with no valid user) for as long as this tab stays open past
    // logout, leaving the refinery showing busy indefinitely.
    this.refineryPresence.stop();
    await this.userService.logoutSession();
    this.clearLocalState();
    this.setLoginStatus(false); // Update login status (also broadcasts to other tabs)
  }
}
